import { Auth } from 'aws-amplify'
import { AmplifyEventBus } from 'aws-amplify-vue'

import constants from '@/constants/constants'
import { setCurrentUserIdentityForAnalytics } from '@/analytics'
import store from '@/store'

export default {
  async signIn(email, password) {
    return new Promise((resolve, reject) => {
      Auth.signIn(email, password)
        .then(user => {
          if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
            Auth.completeNewPassword(user, password, {
              email,
            }).then(() => {
              AmplifyEventBus.$emit('authState', 'signedIn')
              resolve(user)
            })
          } else {
            AmplifyEventBus.$emit('authState', 'signedIn')
            resolve(user)
          }
        })
        .catch(e => {
          reject(e)
        })
    })
  },
  async signUp(name, email, password) {
    return new Promise((resolve, reject) => {
      Auth.signUp({
        username: email,
        password,
        attributes: {
          email,
          picture: constants.DEFAULT_AVATAR,
          name,
        },
      })
        .then(user => {
          resolve(user.user.username)
        })
        .catch(e => {
          if (e.name === 'UsernameExistsException') {
            console.log(e.message)
            Auth.resendSignUp(email)
              .then(() => resolve(email))
              .catch(e => {
                if (e.message === 'User is already confirmed.')
                  reject({ message: 'User is already exist' })
                reject(e)
              })
          } else reject(e)
        })
    })
  },
  async confirmSignUp(username, signUpConfirmationCode, appSumoUser = false) {
    const options = appSumoUser
      ? {
          clientMetadata: {
            appSumoUser: 'true',
          },
        }
      : {}

    return Auth.confirmSignUp(username, signUpConfirmationCode, options)
  },
  async resendConfirmationCode(email) {
    return Auth.resendSignUp(email)
  },
  async signOut() {
    return new Promise((resolve, reject) => {
      console.log('🔄 [LOGOUT DEBUG] Starting comprehensive logout process')
      
      // Emit debug start event
      if (typeof window !== 'undefined' && window.Vue) {
        window.Vue.prototype.$root.$emit('logout-debug-start')
      }
      
      Auth.signOut()
        .then(() => {
          console.log('🔄 [LOGOUT DEBUG] AWS Auth signOut completed')
          
          // 1. Clear localStorage
          console.log('🔄 [LOGOUT DEBUG] Clearing localStorage items')
          const localStorageKeysToRemove = [
            'formId', 
            'userId', 
            'countryCode',
            'aws-amplify-federatedInfo', 
            'aws-amplify-cache',
            'aws-amplify-config'
          ]
          
          localStorageKeysToRemove.forEach(key => {
            if (localStorage.getItem(key)) {
              localStorage.removeItem(key)
              console.log(`🔄 [LOGOUT DEBUG] Removed localStorage key: ${key}`)
            }
          })
          
          // 2. Clear sessionStorage completely
          console.log('🔄 [LOGOUT DEBUG] Clearing sessionStorage')
          sessionStorage.clear()
          
          // 3. Clear IndexedDB (AWS Amplify cache)
          console.log('🔄 [LOGOUT DEBUG] Clearing IndexedDB')
          this.clearIndexedDB()
          
          // 4. Clear cookies
          console.log('🔄 [LOGOUT DEBUG] Clearing cookies')
          this.clearCookies()
          
          // 5. Clear Service Workers
          console.log('🔄 [LOGOUT DEBUG] Clearing Service Workers')
          this.clearServiceWorkers()
          
          // 6. Reset all Vuex stores
          console.log('🔄 [LOGOUT DEBUG] Resetting Vuex stores')
          this.resetAllStores()
          
          // 7. Clear analytics
          console.log('🔄 [LOGOUT DEBUG] Clearing analytics')
          setCurrentUserIdentityForAnalytics(null)
          
          // 8. Emit logout event
          console.log('🔄 [LOGOUT DEBUG] Emitting authState signedOut event')
          AmplifyEventBus.$emit('authState', 'signedOut')
          
          // 9. Verify cleanup completion
          setTimeout(() => {
            this.verifyCleanupCompletion()
          }, 100) // Small delay to allow async operations to complete
          
          console.log('✅ [LOGOUT DEBUG] Comprehensive logout process completed')
          resolve()
        })
        .catch(e => {
          console.error('❌ [LOGOUT DEBUG] Error during logout:', e)
          reject(e)
        })
    })
  },
  
  // Helper method to clear IndexedDB
  clearIndexedDB() {
    try {
      console.log('🔄 [LOGOUT DEBUG] Starting IndexedDB cleanup')
      
      // Clear AWS Amplify IndexedDB databases
      if (window.indexedDB) {
        const amplifyDatabases = [
          'amplify-datastore',
          'amplify-secure-storage',
          'aws-amplify-cache',
          'aws-amplify-storage'
        ]
        
        amplifyDatabases.forEach(dbName => {
          const deleteReq = window.indexedDB.deleteDatabase(dbName)
          deleteReq.onsuccess = () => {
            console.log(`🔄 [LOGOUT DEBUG] IndexedDB ${dbName} cleared successfully`)
          }
          deleteReq.onerror = () => {
            console.log(`🔄 [LOGOUT DEBUG] IndexedDB ${dbName} clear failed`)
          }
        })
      }
      
      console.log('✅ [LOGOUT DEBUG] IndexedDB cleanup completed')
    } catch (error) {
      console.error('❌ [LOGOUT DEBUG] Error clearing IndexedDB:', error)
    }
  },
  
  // Helper method to clear cookies
  clearCookies() {
    try {
      console.log('🔄 [LOGOUT DEBUG] Starting cookie cleanup')
      
      // Get all cookies
      const cookies = document.cookie.split(";")
      
      cookies.forEach(cookie => {
        const eqPos = cookie.indexOf("=")
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim()
        
        if (name) {
          // Clear cookie for multiple domain variations
          const domains = [
            '',
            window.location.hostname,
            '.' + window.location.hostname,
            '.formsly.app',
            '.auth.us-east-1.amazoncognito.com'
          ]
          
          const paths = ['/', '/auth']
          
          domains.forEach(domain => {
            paths.forEach(path => {
              const cookieString = domain 
                ? `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=${domain}`
                : `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}`
              
              document.cookie = cookieString
            })
          })
        }
      })
      
      console.log('✅ [LOGOUT DEBUG] Cookie cleanup completed')
    } catch (error) {
      console.error('❌ [LOGOUT DEBUG] Error clearing cookies:', error)
    }
  },
  
  // Helper method to clear Service Workers
  clearServiceWorkers() {
    try {
      console.log('🔄 [LOGOUT DEBUG] Starting Service Worker cleanup')
      
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          registrations.forEach(registration => {
            registration.unregister().then(success => {
              if (success) {
                console.log('🔄 [LOGOUT DEBUG] Service Worker unregistered successfully')
              } else {
                console.log('🔄 [LOGOUT DEBUG] Service Worker unregistration failed')
              }
            })
          })
        })
      }
      
      console.log('✅ [LOGOUT DEBUG] Service Worker cleanup completed')
    } catch (error) {
      console.error('❌ [LOGOUT DEBUG] Error clearing Service Workers:', error)
    }
  },
  
  // Helper method to reset all stores
  resetAllStores() {
    try {
      const storesToReset = [
        'edit',
        'form_submit', 
        'submissions',
        'forms_list',
        'user',
        'form_templates'
      ]
      
      storesToReset.forEach(storeName => {
        try {
          if (store.hasModule([storeName])) {
            store.dispatch(`${storeName}/RESET`)
            console.log(`🔄 [LOGOUT DEBUG] Reset store: ${storeName}`)
          }
        } catch (error) {
          console.error(`❌ [LOGOUT DEBUG] Error resetting store ${storeName}:`, error)
        }
      })
    } catch (error) {
      console.error('❌ [LOGOUT DEBUG] Error resetting stores:', error)
    }
  },
  
  async forgotPassword(username) {
    return new Promise((resolve, reject) => {
      Auth.forgotPassword(username)
        .then(() => resolve())
        .catch(e => reject(e))
    })
  },
  async forgotPasswordSubmit(username, code, newPassword) {
    return new Promise((resolve, reject) => {
      Auth.forgotPasswordSubmit(username, code, newPassword)
        .then(() => resolve())
        .catch(e => reject(e))
    })
  },
  // async deleteAccount() {
  //     return new Promise((resolve, reject) => {
  //         executeGraphQLMutationDelete(deleteUser, {input: {id: store.getters.getCurrentCognitoUser.username}})
  //             .then(() => {
  //                 return Auth.currentAuthenticatedUser()
  //             })
  //             .then(user => {
  //                 user.deleteUser((error, data) => {
  //                     console.log(data)
  //                     if (error)
  //                         throw error
  //                 })
  //             })
  //             .then(() => {
  //                 return store.dispatch('authUser')
  //             })
  //             .then(() => {
  //                 return AmplifyEventBus.$emit('authState', 'signedOut')
  //             })
  //             .then(() => resolve())
  //             .catch(e => reject(e))
  //     })
  // },

  // async changeEmail() {
  //     // eslint-disable-next-line no-unused-vars
  //     return new Promise((resolve, reject) => {
  //         this.updateUserAttrs({email: 'fdasfasfdsfdsfsda@gmail.com'})
  //     })
  // },
  async changePassword(oldPassword, newPassword) {
    return new Promise((resolve, reject) => {
      Auth.currentAuthenticatedUser()
        .then(user => Auth.changePassword(user, oldPassword, newPassword))
        .then(resolve)
        .catch(reject)
    })
  },

  async updateUserAttrs(attrs) {
    return new Promise((resolve, reject) => {
      Auth.currentAuthenticatedUser()
        .then(user => {
          return Auth.updateUserAttributes(user, attrs)
        })
        .then(resolve)
        .catch(reject)
    })
  },

  async signInGoogle() {
    return new Promise((resolve, reject) => {
      Auth.federatedSignIn({ provider: 'Google' }).then(resolve).catch(reject)
    })
  },

  // Helper method to verify cleanup completion
  verifyCleanupCompletion() {
    try {
      console.log('🔍 [LOGOUT VERIFICATION] Starting cleanup verification')
      
      const verificationResults = {
        localStorage: this.verifyLocalStorageCleanup(),
        sessionStorage: this.verifySessionStorageCleanup(),
        cookies: this.verifyCookieCleanup(),
        indexedDB: this.verifyIndexedDBCleanup(),
        storeModules: this.verifyStoreCleanup()
      }
      
      const allCleared = Object.values(verificationResults).every(result => result)
      
      if (allCleared) {
        console.log('✅ [LOGOUT VERIFICATION] All cleanup verification passed')
      } else {
        console.warn('⚠️ [LOGOUT VERIFICATION] Some cleanup verification failed:', verificationResults)
      }
      
      // Emit verification complete event
      if (typeof window !== 'undefined' && window.Vue) {
        window.Vue.prototype.$root.$emit('logout-verification-complete', verificationResults)
      }
      
      return verificationResults
    } catch (error) {
      console.error('❌ [LOGOUT VERIFICATION] Error during cleanup verification:', error)
      return { error: error.message }
    }
  },
  
  // Verify localStorage cleanup
  verifyLocalStorageCleanup() {
    const keysToCheck = ['formId', 'userId', 'countryCode', 'aws-amplify-federatedInfo']
    const remainingKeys = keysToCheck.filter(key => localStorage.getItem(key) !== null)
    
    if (remainingKeys.length === 0) {
      console.log('✅ [LOGOUT VERIFICATION] localStorage cleanup verified')
      return true
    } else {
      console.warn('⚠️ [LOGOUT VERIFICATION] localStorage cleanup incomplete, remaining keys:', remainingKeys)
      return false
    }
  },
  
  // Verify sessionStorage cleanup
  verifySessionStorageCleanup() {
    const sessionStorageEmpty = sessionStorage.length === 0
    
    if (sessionStorageEmpty) {
      console.log('✅ [LOGOUT VERIFICATION] sessionStorage cleanup verified')
      return true
    } else {
      console.warn('⚠️ [LOGOUT VERIFICATION] sessionStorage cleanup incomplete, remaining items:', sessionStorage.length)
      return false
    }
  },
  
  // Verify cookie cleanup
  verifyCookieCleanup() {
    const remainingCookies = document.cookie.split(';').filter(cookie => cookie.trim().length > 0)
    
    if (remainingCookies.length === 0) {
      console.log('✅ [LOGOUT VERIFICATION] Cookie cleanup verified')
      return true
    } else {
      console.warn('⚠️ [LOGOUT VERIFICATION] Cookie cleanup incomplete, remaining cookies:', remainingCookies.length)
      return false
    }
  },
  
  // Verify IndexedDB cleanup (async check)
  verifyIndexedDBCleanup() {
    // This is a best-effort check since IndexedDB deletion is async
    console.log('🔍 [LOGOUT VERIFICATION] IndexedDB cleanup verification (async process)')
    return true
  },
  
  // Verify store cleanup
  verifyStoreCleanup() {
    try {
      const userState = store.getters['user/getCurrentCognitoUser']
      const cachedCredentials = store.getters['user/getCachedAuthCredentials']
      
      if (userState === undefined && cachedCredentials === undefined) {
        console.log('✅ [LOGOUT VERIFICATION] Store cleanup verified')
        return true
      } else {
        console.warn('⚠️ [LOGOUT VERIFICATION] Store cleanup incomplete')
        return false
      }
    } catch (error) {
      console.error('❌ [LOGOUT VERIFICATION] Error verifying store cleanup:', error)
      return false
    }
  },
}
